import { WheelKind } from 'models/wheel-of-fortune/i-wheel-of-fortune';

/**
 * Per-wheel palettes.
 *
 * One source for every colour either wheel paints, because the same values are needed in
 * three places that cannot see each other: the canvas (2D context, hex strings), the
 * surrounding chrome (Tailwind arbitrary values) and the segment gradients. Hardcoding
 * them per call site is how the regular wheel ended up with its blues written out five
 * times.
 *
 * The VIP wheel reads purple + gold: deep purple carries the shadows, violet and magenta
 * the mid-tones, and gold is reserved for what the eye should land on — the winning
 * segment and its glow. Gold is never used as a segment fill, only as a highlight, so it
 * stays the brightest thing on the wheel.
 */
export interface WheelPalette {
  /** Segment fill, alternating. Three stops each: outer-transparent -> mid -> solid rim. */
  segmentA: [string, string, string];
  segmentB: [string, string, string];
  /** Winning-segment edge and its glow. */
  highlight: string;
  /** Text on the winning segment; every other segment is `text`. */
  highlightText: string;
  text: string;
  /** Canvas disc behind the segments, as a Tailwind arbitrary value. */
  canvasBg: string;
  /**
   * Fill behind the available-spins badge, and nothing else. Button and switch chrome is
   * class-based — `.wof-btn--primary` / `.wof-btn--amber` in WheelOfFortuneModal.scss are
   * the source of truth there, so do not add button colours here expecting them to apply.
   */
  badgeBg: string;
}

export const WHEEL_PALETTES: Record<WheelKind, WheelPalette> = {
  regular: {
    segmentA: ['#0E265933', '#0E2659CC', '#0E2659'],
    segmentB: ['#14347833', '#143478CC', '#143478'],
    highlight: '#6DFBFF',
    highlightText: '#6DFBFF',
    text: '#FFFFFF',
    canvasBg: 'tw-bg-menuLinkColor',
    badgeBg: '#1F408C',
  },
  vip: {
    // Deep purple shadow -> violet body.
    segmentA: ['#2A0A4E33', '#2A0A4ECC', '#2A0A4E'],
    // Violet -> magenta, the secondary pairing.
    segmentB: ['#7B1FA233', '#7B1FA2CC', '#8E24AA'],
    // Bright gold, highlight only.
    highlight: '#FFC83D',
    highlightText: '#FFD76E',
    text: '#F6E6FF',
    canvasBg: 'tw-bg-[#1A0630]',
    badgeBg: '#3B0A63',
  },
};

export const paletteFor = (kind: WheelKind): WheelPalette => WHEEL_PALETTES[kind];
